import {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import type {Category, SubSubcategory, Subcategory} from "@corporatesclub/shared";
import {paths} from "@/lib/paths";
import {useI18n} from "./I18nProvider";
import {getCategoriesCached} from "@/lib/shopCache";

type CatTreeNode = {
  id: number;
  nume: string;
  url: string;
  children?: CatTreeNode[];
};

function treeChildren(item: Category | Subcategory | SubSubcategory | {subsubsubcategorii?: CatTreeNode[]}): CatTreeNode[] {
  const c = item as Category & Subcategory & SubSubcategory & {subsubsubcategorii?: CatTreeNode[]};
  const raw = c.subcategorii || c.subsubcategorii || c.subsubsubcategorii || [];
  return raw.map((n) => ({
    id: n.id,
    nume: n.nume,
    url: n.url,
    children: treeChildren(n as Subcategory).length ? treeChildren(n as Subcategory) : undefined
  }));
}

function FilterCatNode({node, depth = 0}: {node: CatTreeNode; depth?: number}) {
  const [open, setOpen] = useState(false);
  const hasChildren = !!node.children?.length;
  return (
    <li className="cc-filters__catItem">
      <div className="cc-filters__catRow" style={{paddingLeft: depth ? depth * 10 : 0}}>
        {hasChildren ? (
          <button type="button" className="cc-filters__catToggle" onClick={() => setOpen(!open)} aria-expanded={open} aria-label={node.nume}>
            <i className={`fa fa-chevron-${open ? "down" : "right"}`} aria-hidden="true" />
          </button>
        ) : (
          <span className="cc-filters__catToggle cc-filters__catToggle--spacer" aria-hidden="true" />
        )}
        <Link to={node.url} className="cc-filters__catLink">{node.nume}</Link>
      </div>
      {hasChildren && open ? (
        <ul className="cc-filters__catSub">
          {node.children!.map((c) => <FilterCatNode key={c.id} node={c} depth={depth + 1} />)}
        </ul>
      ) : null}
    </li>
  );
}

export function CategoryMenu({
  compact = false,
  headerScoped = false,
  filterScoped = false,
  open = true,
  onToggle
}: {
  compact?: boolean;
  headerScoped?: boolean;
  filterScoped?: boolean;
  open?: boolean;
  onToggle?: () => void;
}) {
  const {t} = useI18n();
  const [items, setItems] = useState<Category[]>([]);
  useEffect(() => { getCategoriesCached().then(setItems).catch(() => {}); }, []);
  if(!items.length) return null;
  if(compact) {
    if(headerScoped) {
      return (
        <nav className="cc-header__nav" aria-label={t("CATEGORIES")}>
          <div className="cc-header__container">
            <ul className="cc-header__navList">
              {items.map((c) => (
                <li key={c.id}><Link to={c.url} className="cc-header__navLink">{c.nume}</Link></li>
              ))}
              <li><Link to={paths.all} className="cc-header__navLink">{t("All products")}</Link></li>
            </ul>
          </div>
        </nav>
      );
    }
    return (
      <div id="menu">
        <div className="inside">
          <div id="mainMenu">
            <ul className="clearfix">
              {items.map((c) => (
                <li key={c.id}><Link to={c.url} className="anim">{c.nume}</Link></li>
              ))}
              <li><Link to={paths.all} className="anim">{t("All products")}</Link></li>
            </ul>
          </div>
        </div>
      </div>
    );
  }
  if(filterScoped) {
    const tree: CatTreeNode[] = [
      ...items.map((c) => {
        const children = treeChildren(c);
        return {
          id: c.id,
          nume: c.nume,
          url: c.url,
          children: children.length ? children : undefined
        };
      }),
      {id: -1, nume: t("All products"), url: paths.all}
    ];
    return (
      <div className="cc-filters__section">
        <button type="button" className="cc-filters__sectionHead" onClick={onToggle} aria-expanded={open}>
          <span>{t("CATEGORIES")}</span>
          <span className="cc-filters__sectionMeta">
            <span className="cc-filters__sectionToggle" aria-hidden="true">{open ? "−" : "+"}</span>
          </span>
        </button>
        {open ? (
          <div className="cc-filters__sectionBody">
            <ul className="cc-filters__catList cc-filters__catScroll">
              {tree.map((c) => <FilterCatNode key={c.id} node={c} />)}
            </ul>
          </div>
        ) : null}
      </div>
    );
  }
  return (
    <div className="filtru">
      <h3><span>{t("CATEGORIES")}</span></h3>
      <div className="optiuni dynCats">
        <ul>
          {items.map((c) => (
            <li key={c.id}>
              <Link to={c.url} className="anim main">{c.nume}</Link>
              {c.subcategorii?.length ? (
                <ul className="submenu">
                  {c.subcategorii.map((sc) => (
                    <li key={sc.id}>
                      <Link to={sc.url} className="anim">{sc.nume}</Link>
                      {sc.subsubcategorii?.length ? (
                        <ul className="submenu">
                          {sc.subsubcategorii.map((ssc) => (
                            <li key={ssc.id}>
                              <Link to={ssc.url} className="anim">{ssc.nume}</Link>
                              {ssc.subsubsubcategorii?.length ? (
                                <ul className="submenu">
                                  {ssc.subsubsubcategorii.map((sssc) => (
                                    <li key={sssc.id}><Link to={sssc.url} className="anim">{sssc.nume}</Link></li>
                                  ))}
                                </ul>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
          <li><Link to={paths.all} className="anim main">{t("All products")}</Link></li>
        </ul>
      </div>
    </div>
  );
}
